import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { warehouseAPI } from '../../api/warehouses';
import { X, Sparkles, Check } from 'lucide-react';
import toast from 'react-hot-toast';

interface CalculatedThreshold {
  itemId: number;
  itemName: string;
  avgMonthlyOutbound: number;
  suggestedMin: number;
  suggestedMax: number;
  currentMin: number | null;
  currentMax: number | null;
  hasManualOverride: boolean;
  currentStock: number;
  unit: string;
}

interface AutoCalculateModalProps {
  warehouseId: number;
  onClose: () => void;
  onApplied: () => void;
}

export const AutoCalculateModal: React.FC<AutoCalculateModalProps> = ({
  warehouseId,
  onClose,
  onApplied,
}) => {
  const { t } = useTranslation();
  const [calculated, setCalculated] = useState<CalculatedThreshold[]>([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [overwriteManual, setOverwriteManual] = useState(false);

  useEffect(() => {
    const loadCalculated = async () => {
      try {
        setLoading(true);
        const result = await warehouseAPI.calculateThresholds(warehouseId);
        setCalculated(result);
      } catch (error) {
        console.error('Failed to calculate thresholds:', error);
        toast.error(t('common.error'));
      } finally {
        setLoading(false);
      }
    };

    loadCalculated();
  }, [warehouseId]);

  const handleApply = async () => {
    try {
      setApplying(true);
      const result = await warehouseAPI.applyAutoThresholds(warehouseId, overwriteManual);
      toast.success(
        t('warehouse.stockAlerts.appliedCount', { count: result.appliedCount })
      );
      onApplied();
    } catch (error) {
      console.error('Failed to apply thresholds:', error);
      toast.error(t('common.error'));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-5xl rounded-lg bg-white p-6 shadow-xl">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="h-6 w-6 text-purple-600" />
            <h2 className="text-xl font-bold text-gray-900">
              {t('warehouse.stockAlerts.autoCalculate')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Description */}
        <p className="mb-4 text-sm text-gray-600">
          {t('warehouse.stockAlerts.autoCalculateDescription')}
        </p>

        {/* Loading State */}
        {loading ? (
          <div className="animate-pulse space-y-4 py-8">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 rounded bg-gray-200"></div>
            ))}
          </div>
        ) : calculated.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-gray-600">
              {t('warehouse.stockAlerts.noDataForCalculation')}
            </p>
          </div>
        ) : (
          <>
            {/* Preview Table */}
            <div className="mb-4 max-h-96 overflow-y-auto rounded-lg border">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="sticky top-0 bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      {t('item.item')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      {t('warehouse.stockAlerts.currentStock')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      {t('warehouse.stockAlerts.avgOutbound')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      {t('warehouse.stockAlerts.currentMin')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      {t('warehouse.stockAlerts.suggestedMin')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      {t('warehouse.stockAlerts.currentMax')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      {t('warehouse.stockAlerts.suggestedMax')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {calculated.map((calc) => (
                    <tr
                      key={calc.itemId}
                      className={calc.hasManualOverride ? 'bg-yellow-50' : ''}
                    >
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                        {calc.itemName}
                        {calc.hasManualOverride && (
                          <span className="ml-2 rounded bg-yellow-200 px-1 py-0.5 text-xs">
                            {t('warehouse.stockAlerts.manual')}
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                        {calc.currentStock.toFixed(2)} {calc.unit}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                        {calc.avgMonthlyOutbound.toFixed(2)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                        {calc.currentMin?.toFixed(2) || '-'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-green-600">
                        {calc.suggestedMin.toFixed(2)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                        {calc.currentMax?.toFixed(2) || '-'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-green-600">
                        {calc.suggestedMax.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Overwrite Option */}
            <div className="mb-4 rounded-lg bg-yellow-50 p-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={overwriteManual}
                  onChange={(e) => setOverwriteManual(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  {t('warehouse.stockAlerts.overwriteManual')}
                </span>
              </label>
              <p className="ml-6 mt-1 text-xs text-gray-600">
                {t('warehouse.stockAlerts.overwriteManualDesc')}
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                disabled={applying}
                className="rounded-lg bg-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-400 disabled:bg-gray-200"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleApply}
                disabled={applying}
                className="flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 font-medium text-white hover:bg-purple-700 disabled:bg-purple-300"
              >
                {applying ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    {t('common.applying')}
                  </>
                ) : (
                  <>
                    <Check className="h-5 w-5" />
                    {t('warehouse.stockAlerts.applyAutoThresholds')}
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
